import { Fetch } from '../middleware/fetch';
import ENV from '../../env';
import { normalize } from 'normalizr';
import { url as formatUrl } from '../../util/env';
import url from 'url';

const isJSON = type => type && type.indexOf('application/json') !== -1;
const isText = type => type && type.indexOf('text/') !== -1;
const passthrough = input => input;
const GET_TRANSACTION = 'GET_TRANSACTION';
const INVALIDATION_ERROR = /(Unauthorized|Forbidden|No user found|Missing x-user-key")/i;

export class API extends Fetch {
  constructor(basepath, defaults = {}, options = {}) {
    super(basepath, defaults, options);
    this.buildTransactionTypes();
  }

  buildTransactionTypes() {
    const [t, r, s, f] = this.buildTypes(GET_TRANSACTION);
    this.GET_TRANSACTION = t;
    this.GET_TRANSACTION_REQUEST = r;
    this.GET_TRANSACTION_SUCCESS = s;
    this.GET_TRANSACTION_FAILURE = f;
  }

  getTransaction(transactionID, schema) {
    return {
      type: this.GET_TRANSACTION,
      payload: {
        url: `/await/${encodeURIComponent(transactionID)}`,
        schema: schema
      }
    };
  }
  // eslint-disable-next-line no-unused-vars
  before(action, states, next) {
    const { payload: { json = true, options: { body } = {} } = {} } = action;
    const nextAction = { ...action };
    if (json && body !== undefined) {
      nextAction.payload.options.body = JSON.stringify(body);
    }

    return Promise.resolve(nextAction);
  }

  after(action, states, next, store) {
    const { payload, response } = action;
    const type = response.headers.get('content-type');

    const makeError = responseBody => {
      const method = this.getRequestMethod(action);
      const err = new Error(
        `${method} ${response.url} returned ${response.status}: ${responseBody}`
      );
      err.response = response;
      err.status = response.status;

      return err;
    };

    const maybeInvalidateAuth = message => {
      if (response.status === 403 || INVALIDATION_ERROR.test(message)) {
        store.dispatch({ type: 'AUTHENTICATION_INVALIDATE' });
      }
    };

    return isJSON(type)
      ? response
          .json()
          .then(data => {
            maybeInvalidateAuth(data && data.message);
            return data;
          })
          .then(data => {
            if (!response.ok || (data && data.errorMessage)) {
              // eslint-disable-next-line no-unused-vars
              const method = (payload.options || {}).method || 'GET';
              const err = makeError(JSON.stringify(data));
              err.responseJSON = data;
              throw err;
            }

            const { transform = passthrough } = payload;
            return transform(data);
          })
          .then(data => {
            if (!data) {
              return {
                ...action,
                response: null // we should be returning the actual response here!
              };
            }

            const { schema } = payload;
            const transactionID = (data.transaction || {}).id;

            let transaction;
            if ((transactionID || transactionID === 0) && schema) {
              transaction = store.dispatch(
                this.getTransaction(transactionID, schema)
              );
            }

            let json = data;
            const requestUrl = url.parse(payload.url || '/', true);

            const PAGING_ENDPOINTS = ['tasks', 'processes', 'credentials'];

            // API will be transitioning to mandatory paging for all list endpoints, but for now it's only enabled
            // on tasks and processes and credentials
            const usingPaging =
              requestUrl.query.limit ||
              PAGING_ENDPOINTS.some(endpoint =>
                requestUrl.pathname.endsWith(`/${endpoint}`)
              );

            const searching = requestUrl.pathname.endsWith('/search');
            const tableRows = requestUrl.pathname.endsWith('/rows');

            const pagingAPI = data && data.items && usingPaging && !searching;
            if (pagingAPI) {
              json = data.items;
            } else if (tableRows && data.results) {
              json = data.results;
            }

            const result =
              !schema || typeof json !== 'object'
                ? json
                : normalize(json, schema);

            if (typeof result === 'object') {
              if (pagingAPI) {
                result.nextPageKey = data.nextPageKey;
              } else if (searching) {
                result.nextPageKey = data.pageToken;
                result.result = data.results.map(r => ({
                  id: r.id,
                  schema: r.type
                }));
                result.total = data.total;
              } else if (tableRows) {
                result.pageToken = data.pageToken;
                result.total = data.total;
              }
            }

            const response = {
              ...action,
              response: result
            };
            if (transaction) {
              response.transaction = transaction;
            }

            return response;
          })
      : isText(type)
        ? response
            .text()
            .then(data => {
              maybeInvalidateAuth(data);
              return data;
            })
            .then(data => {
              if (!response.ok) {
                const err = makeError(data);
                err.responseText = data;
                throw err;
              }

              const { transform = passthrough } = payload;
              return transform(data);
            })
            .then(response => ({ ...action, response }))
        : response
            .blob()
            .then(data => {
              maybeInvalidateAuth(data);
              return data;
            })
            .then(data => {
              if (!response.ok) {
                const err = makeError('');
                err.responseBlob = data;
                throw err;
              }

              const { transform = passthrough } = payload;
              return transform(data);
            })
            .then(response => ({ ...action, response }));
  }

  getRequestMethod(action) {
    const { payload = {} } = action;
    return (payload.options || {}).method || 'GET';
  }

  actionCanBeRetried(action) {
    const { payload = {} } = action;
    const retryOption = (payload.options || {}).retry || false;
    return (
      ['GET', 'HEAD', 'OPTIONS'].includes(this.getRequestMethod(action)) ||
      retryOption
    );
  }

  maxRetries(action, response) {
    if (this.actionCanBeRetried(action) && response.status >= 500) {
      return 3;
    }
    return 0;
  }
  // eslint-disable-next-line no-unused-vars
  error(action, states, next, store) {
    const [, , FAILURE] = states;
    next({ ...action, type: FAILURE });
    return Promise.reject(action.error);
  }
}

const api = new API(
  formatUrl(ENV),
  {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(ENV.PUSHBOT_API_KEY
        ? {
            'x-api-key': ENV.PUSHBOT_API_KEY
          }
        : {})
    },
    method: 'GET'
  },
  { prefix: 'API' }
);

export default api;



// WEBPACK FOOTER //
// ./js/store/config/api.js